import * as React from 'react';

import {copyToClipboard} from 'nutshell-core/utils/copy-to-clipboard';

import {EditionThumbnailCard} from 'shells/edition-thumbnail-list/edition-thumbnail-card';
import {GridList} from 'shells/grid-list/grid-list';
// @ts-expect-error - imported from .js/.jsx file
import {ArchiveIcon} from 'shells/icon/svg/archive-icon';
// @ts-expect-error - imported from .js/.jsx file
import {CloneIcon} from 'shells/icon/svg/clone-icon';
// @ts-expect-error - imported from .js/.jsx file
import {ExternalLinkIcon} from 'shells/icon/svg/external-link-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EyeIcon} from 'shells/icon/svg/eye-icon';

import type {BroadcastStubFragment as Broadcast} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import {EmptyState} from './empty-state';

const CARD_WIDTH = 300;
const CARD_HEIGHT = 300;

type Props = {
    broadcasts: Broadcast[];
    onDeleteBroadcast: (arg1: string) => Promise<any>;
    onRestoreBroadcast: (arg1: string) => Promise<any>;
    isLoading: boolean;
    isFiltering: boolean;
    error: string | null | undefined;
    setDuplicatingBroadcast: (arg1?: Broadcast | null | undefined) => void;
    setIsAddingNewBroadcast: (arg1: boolean) => void;
    hasFullAccessToCampaigns: boolean;
};

export function BroadcastsList(props: Props) {
    return (
        <GridList
            isLoading={props.isLoading}
            itemWidth={CARD_WIDTH}
            loadingState={{itemHeight: CARD_HEIGHT, itemBackgroundColor: 'white'}}
            error={props.error}
            isFiltering={props.isFiltering}
            nothingFoundState={{title: 'No broadcasts were found'}}
            emptyStateComponent={
                <EmptyState onPrimaryCtaClick={() => props.setIsAddingNewBroadcast(true)} />
            }
        >
            {props.broadcasts
                ? props.broadcasts
                      .map((broadcast) => {
                          const options = [
                              {
                                  label: 'View',
                                  handleSelect: () => {
                                      // broadcast.edition is the A edition which is what we should link to
                                      appHistory.push(broadcast.edition.htmlUrlPath);
                                  },
                                  icon: <EyeIcon wrapWithDiv={false} size={18} />,
                              },
                          ];

                          const editionToDisplay =
                              broadcast.edition &&
                              broadcast.edition.comparison &&
                              broadcast.edition.comparison.winnerEdition
                                  ? broadcast.edition.comparison.winnerEdition
                                  : broadcast.edition;

                          // @ts-expect-error
                          if (!editionToDisplay.deletedTime) {
                              options.push({
                                  label: 'Make a copy',
                                  handleSelect: () => {
                                      props.setDuplicatingBroadcast(broadcast);
                                  },
                                  icon: <CloneIcon size={16} />,
                              });
                              options.push({
                                  label: 'Copy shareable URL',
                                  handleSelect: () => {
                                      copyToClipboard(editionToDisplay.viewInBrowserLink);
                                  },
                                  // @ts-expect-error
                                  tooltipTextOnClose: 'Copied!',
                                  icon: <ExternalLinkIcon size={16} />,
                              });
                              options.push({
                                  label: 'Archive',
                                  // @ts-expect-error
                                  variant: 'destructive',
                                  handleSelect: () => {
                                      props.onDeleteBroadcast(broadcast.id);
                                  },
                                  icon: <ArchiveIcon size={16} />,
                              });
                          }

                          // @ts-expect-error
                          if (editionToDisplay.deletedTime) {
                              options.push({
                                  label: 'Restore',
                                  handleSelect: () => {
                                      props.onRestoreBroadcast(broadcast.id);
                                  },
                                  icon: <ArchiveIcon size={16} />,
                              });
                          }

                          return {
                              id: broadcast.id,
                              createdTime: broadcast.edition.createdTime, // broadcast.edition is A edition, always use that created time
                              sendTime: editionToDisplay.sendTime,
                              // @ts-expect-error
                              status: editionToDisplay.deletedTime
                                  ? 'ARCHIVED'
                                  : editionToDisplay.status,
                              name: broadcast.edition.name,
                              owner: props.hasFullAccessToCampaigns
                                  ? broadcast.edition.campaign.owner
                                  : undefined,
                              isABTesting: broadcast.edition.isABTesting,
                              thumbnailUrl:
                                  editionToDisplay &&
                                  editionToDisplay.emailTemplate &&
                                  editionToDisplay.emailTemplate.thumbnailUrl
                                      ? editionToDisplay.emailTemplate.thumbnailUrl
                                      : undefined,
                              options: options,
                              campaignType: 'BROADCAST',
                              backgroundColor: broadcast.edition.backgroundColor,
                          };
                      })
                      .map((broadcast) => (
                          <EditionThumbnailCard
                              key={broadcast.id}
                              edition={broadcast}
                              onSelect={(broadcastId) => {
                                  const selectedBroadcast = props.broadcasts.find(
                                      (broadcastItem) => {
                                          return broadcastItem.id === broadcastId;
                                      }
                                  );

                                  if (selectedBroadcast) {
                                      appHistory.push(selectedBroadcast.edition.htmlUrlPath);
                                  }
                              }}
                          />
                      ))
                : null}
        </GridList>
    );
}
